import { ref, ssrRef, useRoute } from '@nuxtjs/composition-api';
import { useFacet, useUiHelpers } from '~/composables';
import type { ProductInterface, Aggregation, Products } from '~/modules/GraphQL/types';
import type { Product } from '~/modules/catalog/product/types';
import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Pagination } from '~/composables/types';
import facetGetters from '~/modules/catalog/category/getters/facetGetters';
import { useOmnibusPrice } from '~/modules/catalog/product/composables/useOmnibusPrice';
import { useApi } from '~/composables/useApi';
import GetProductFilterByCategoryQuery from '~/modules/catalog/category/components/filters/command/getProductFilterByCategory.gql';

export const useCatalogProducts = () => {
  const products = ssrRef<ProductInterface[]>([]);
  const extendedProducts = ref<ProductInterface[]>([]);
  const filters = ssrRef<Aggregation[]>([]);
  const sortBy = ref<SortingModel>({ selected: '', options: [] });
  const pagination = ref<Pagination>({ currentPage: 0, totalPages: 0 });

  const route = useRoute();
  const { query } = useApi();
  const { search, result } = useFacet();
  const uiHelpers = useUiHelpers();
  const { getOmnibusPrices } = useOmnibusPrice();

  const fetchFilters = async (categoryUid) => {
    const searchVars = {
      filter: {
        category_uid: { eq: categoryUid },
      },
      search: route.value?.query?.search || '',
    };
    const { data } = await query<{ products: Products }>(GetProductFilterByCategoryQuery, searchVars);
    filters.value = data?.products?.aggregations ?? [];
  };

  const fetchProducts = async (categoryUid, isGridView: boolean) => {
    await search({
      ...uiHelpers.getFacetsFromURL(),
      category_uid: categoryUid,
      isExtended: !isGridView,
    });

    const productsData = facetGetters.getProducts(result.value) ?? [];
    if (isGridView) {
      products.value = productsData;
    } else {
      extendedProducts.value = productsData;
    }

    const omnibusStrategy = isGridView ? 'cheapest' : 'all';
    await getOmnibusPrices(productsData as Product[], omnibusStrategy);

    sortBy.value = facetGetters.getSortOptions(result.value);
    pagination.value = facetGetters.getPagination(result.value);
    await fetchFilters(categoryUid);
  };

  return {
    products,
    extendedProducts,
    filters,
    sortBy,
    pagination,
    fetchProducts,
  };
};
